import React from "react";
import {
  Sparkles, Zap, Lock, MessageCircle, Gamepad2, ArrowRight, ShieldCheck,
  BadgeDollarSign, HeadphonesIcon, Award, CreditCard, CheckCircle2, Crown, Gift,
} from "lucide-react";
import { GameCard, GameCardSkeleton } from "../components/game/GameCard";
import { thumb400 } from "../lib/imageVariants";
import * as api from "../api";

/**
 * GameCatalogPage — the browsable list of games ("SELECCIONA TU JUEGO").
 *
 * Extracted from App.js to isolate the catalog grid + trust badges from the
 * heavy package-selection view that follows once a game is picked. Package
 * selection stays inside App.js for now because it depends on ~15 pieces of
 * state (cart, credentials, verification) that aren't worth prop-drilling.
 *
 * Side-effects (game selection hydration + inactive toast) are handled by
 * callbacks so this component stays free of module-level state.
 */
export function GameCatalogPage({
  games,
  gamesLoaded,
  setGames,
  setSelectedGame,
  setSelectedPackage,
  setPlayerId,
  setPlayerPassword,
  setPlayerServer,
  setAccessMethod,
  setVerifiedPlayerName,
  setIdVerificationStatus,
  showToast,
}) {
  const handleSelectGame = async (g) => {
    setSelectedGame(g);
    setSelectedPackage(null);
    setPlayerId("");
    setPlayerPassword("");
    setPlayerServer("");
    setAccessMethod("");
    setVerifiedPlayerName(null);
    setIdVerificationStatus("idle");
    // Hydrate package images on demand (Home fetches the light catalog for
    // speed; the heavier assets arrive here right before the user needs them).
    const needsHydration = (g.packages || []).length > 0 && !(g.packages || []).some((p) => p.image);
    if (needsHydration) {
      try {
        const pkgs = await api.listGamePackages(g.id);
        setSelectedGame((prev) => (prev && prev.id === g.id ? { ...prev, packages: pkgs } : prev));
        setGames((prev) => prev?.map((x) => (x.id === g.id ? { ...x, packages: pkgs } : x)));
      } catch (e) {
        console.error("Failed to hydrate packages for game", g.id, e);
      }
    }
  };

  const handleInactive = (g) =>
    showToast("No disponible", `${g.name} está temporalmente fuera de servicio. Reintenta más tarde.`, "warning");

  return (
    <div className="space-y-10 sm:space-y-16 py-2 sm:py-4">
      <HomeHero games={games} />

      {/* NUESTROS JUEGOS + panel lateral de confianza (reference composition) */}
      <section id="ag7-games" data-testid="home-games-section" className="scroll-mt-24 grid xl:grid-cols-[1fr_300px] gap-6 xl:gap-8 items-start">
        <div className="space-y-5 sm:space-y-7 min-w-0">
          <div className="flex items-end justify-between gap-4 px-1">
            <div className="flex items-center gap-3 sm:gap-4 text-left">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-ag7-purple/15 border border-ag7-purple/50 shadow-[0_0_18px_rgba(147,51,234,0.25)] flex items-center justify-center flex-shrink-0" aria-hidden="true">
                <Gamepad2 className="w-5 h-5 sm:w-6 sm:h-6 text-ag7-purple" />
              </div>
              <div>
                <h2 className="font-heading text-xl sm:text-3xl font-black text-white tracking-wide uppercase">Nuestros Juegos</h2>
                <p className="text-[11px] sm:text-xs text-[var(--ag7-text-muted)]">Selecciona tu juego y encuentra el paquete ideal para ti</p>
              </div>
            </div>
            <Sparkles className="hidden sm:block w-5 h-5 text-ag7-magenta flex-shrink-0 mb-1" aria-hidden="true" />
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-5">
            {games.length === 0 && !gamesLoaded
              ? Array.from({ length: 8 }).map((_, i) => <GameCardSkeleton key={`sk-${i}`} />)
              : games?.map((game) => (
                  <GameCard key={game.id} game={game} onSelect={handleSelectGame} onInactive={handleInactive} />
                ))}
          </div>
        </div>

        <aside className="space-y-5 xl:sticky xl:top-24">
          <WhyAG7 />
          <PromoBlock />
        </aside>
      </section>

      <HowItWorks />
      <HomeFooter />
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * HERO — cinematic full-width scene. Left: headline + CTA + trust badges.
 * Right: layered collage built from the REAL game artwork already loaded.
 * Pure presentation: the only interaction is a smooth-scroll to the catalog.
 * ------------------------------------------------------------------------- */
function HomeHero({ games }) {
  const featured = (games || []).filter((g) => g.image).slice(0, 5);
  const scrollToGames = () => document.getElementById("ag7-games")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <section
      data-testid="home-hero"
      className="relative overflow-hidden rounded-3xl lg:min-h-[72vh] flex items-center bg-[#0a0812] shadow-[0_40px_100px_-40px_rgba(147,51,234,0.35)] animate-in fade-in slide-in-from-bottom-4 duration-700"
    >
      {/* Cinematic nebula lighting */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_70%_at_70%_30%,rgba(147,51,234,0.28),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_60%_at_15%_80%,rgba(236,72,153,0.16),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_50%_50%_at_90%_85%,rgba(6,182,212,0.14),transparent_60%)]" />
        <div className="absolute -top-40 right-[20%] w-[520px] h-[520px] rounded-full bg-ag7-purple/20 blur-3xl" />
        <div className="absolute -bottom-48 -left-24 w-[420px] h-[420px] rounded-full bg-ag7-magenta/12 blur-3xl" />
        {/* subtle grid texture */}
        <div className="absolute inset-0 opacity-[0.05] bg-[linear-gradient(rgba(147,51,234,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(147,51,234,0.6)_1px,transparent_1px)] bg-[size:48px_48px]" />
        {/* diagonal energy beam */}
        <div className="absolute top-0 right-[30%] w-[2px] h-full bg-gradient-to-b from-transparent via-ag7-purple/40 to-transparent rotate-[18deg] origin-top" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_50%,rgba(9,9,11,0.7)_100%)]" />
      </div>

      <div className="relative w-full grid lg:grid-cols-[1.05fr_1fr] gap-8 lg:gap-2 items-center px-5 py-10 sm:px-10 sm:py-14 lg:py-16">
        {/* LEFT — headline + CTA */}
        <div className="text-left space-y-5 sm:space-y-7">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-ag7-purple/15 border border-ag7-purple/50 shadow-[0_0_20px_rgba(147,51,234,0.25)]" data-testid="hero-eyebrow">
            <Crown className="w-3.5 h-3.5 text-ag7-magenta" aria-hidden="true" />
            <span className="text-[10px] sm:text-[11px] font-mono font-bold uppercase tracking-[0.3em] text-purple-200">AG7 Store · Tienda Gaming</span>
          </div>

          <h1 className="font-heading font-black leading-[0.92] tracking-tight text-[2.6rem] sm:text-6xl lg:text-7xl" data-testid="hero-title">
            <span className="block text-white drop-shadow-[0_4px_24px_rgba(147,51,234,0.45)]">RECARGA TUS</span>
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-ag7-purple via-ag7-magenta to-ag7-cyan drop-shadow-[0_4px_28px_rgba(236,72,153,0.35)]">JUEGOS FAVORITOS</span>
          </h1>

          <p className="text-base sm:text-lg text-zinc-300 font-medium tracking-wide" data-testid="hero-subtitle">
            Rápido <span className="text-ag7-purple">·</span> Seguro <span className="text-ag7-magenta">·</span> Sin Complicaciones
          </p>

          <button
            onClick={scrollToGames}
            data-testid="hero-cta-btn"
            className="group inline-flex items-center gap-3 px-9 py-4 rounded-2xl bg-gradient-to-r from-ag7-purple to-ag7-magenta text-white font-heading font-black text-base sm:text-lg tracking-wide shadow-[0_10px_40px_-8px_rgba(147,51,234,0.7)] ring-1 ring-ag7-magenta/50 hover:shadow-[0_0_50px_rgba(236,72,153,0.6)] hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200"
          >
            ¡Recarga Ahora!
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform" aria-hidden="true" />
          </button>

          <TrustBadgesStrip />
        </div>

        {/* RIGHT — layered collage with real game artwork */}
        <div className="relative h-64 sm:h-80 lg:h-[30rem] select-none" data-testid="hero-artwork" aria-hidden="true">
          {featured.length > 0 ? (
            <>
              {/* halo behind main art */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 lg:w-96 lg:h-96 rounded-full bg-ag7-purple/25 blur-3xl" />
              {/* main artwork card */}
              <div className="absolute left-[42%] top-1/2 -translate-x-1/2 -translate-y-1/2 w-44 sm:w-56 lg:w-72 aspect-[4/5] rounded-3xl overflow-hidden border-2 border-ag7-purple/70 shadow-[0_0_70px_rgba(147,51,234,0.55)] -rotate-3 z-20 hover:rotate-0 transition-transform duration-500">
                <img src={thumb400(featured[0].image)} alt="" className="w-full h-full object-cover" loading="eager" fetchPriority="high" decoding="async" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-ag7-purple/20" />
                <p className="absolute bottom-4 inset-x-4 font-heading font-black text-white text-base sm:text-xl uppercase tracking-wide drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]">{featured[0].name}</p>
              </div>
              {/* secondary artwork */}
              {featured[1] && (
                <div className="absolute left-[2%] top-[6%] w-28 sm:w-36 lg:w-44 aspect-square rounded-2xl overflow-hidden border border-ag7-cyan/60 shadow-[0_0_40px_rgba(6,182,212,0.45)] rotate-6 z-10">
                  <img src={thumb400(featured[1].image)} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                </div>
              )}
              {featured[2] && (
                <div className="absolute left-[8%] bottom-[2%] w-24 sm:w-32 lg:w-40 aspect-square rounded-2xl overflow-hidden border border-ag7-magenta/60 shadow-[0_0_40px_rgba(236,72,153,0.4)] -rotate-6 z-10">
                  <img src={thumb400(featured[2].image)} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                </div>
              )}
              {/* floating name plates (reference-style angled chips) */}
              <div className="hidden sm:flex flex-col gap-2.5 absolute right-0 top-[12%] z-30 items-end">
                {featured.slice(0, 4).map((g, i) => (
                  <div
                    key={g.id}
                    className={`px-4 py-2 rounded-xl bg-[var(--ag7-glass)] backdrop-blur-md border text-[10px] lg:text-xs font-heading font-extrabold uppercase tracking-widest text-white ${
                      i % 3 === 0
                        ? "border-ag7-purple/60 shadow-[0_0_18px_rgba(147,51,234,0.35)] -rotate-2"
                        : i % 3 === 1
                        ? "border-ag7-cyan/60 shadow-[0_0_18px_rgba(6,182,212,0.3)] rotate-1"
                        : "border-ag7-magenta/60 shadow-[0_0_18px_rgba(236,72,153,0.3)] -rotate-1"
                    }`}
                  >
                    {g.name}
                  </div>
                ))}
              </div>
              {/* sparks */}
              <Zap className="absolute left-[0%] bottom-[26%] w-7 h-7 text-ag7-cyan/80 rotate-12 drop-shadow-[0_0_10px_rgba(6,182,212,0.8)]" fill="currentColor" strokeWidth={0} />
              <Sparkles className="absolute left-[55%] top-[0%] w-6 h-6 text-ag7-magenta/80 drop-shadow-[0_0_10px_rgba(236,72,153,0.8)]" />
              <div className="absolute right-[18%] bottom-[10%] w-3 h-3 rounded-full bg-ag7-purple shadow-[0_0_16px_rgba(147,51,234,0.9)]" />
            </>
          ) : (
            <div className="absolute inset-6 rounded-3xl bg-ag7-surface-2/60 border border-ag7-purple/20 animate-pulse" />
          )}
        </div>
      </div>
    </section>
  );
}

/** Reinforces confidence in delivery, security & support before the buyer commits. */
function TrustBadgesStrip() {
  const badges = [
    { key: "delivery", accent: "cyan",    Icon: Zap,            iconFill: true,  title: "Atención Rápida",  body: "Procesamiento manual en menos de 5 min (en horario activo)" },
    { key: "payments", accent: "green",   Icon: Lock,           iconFill: false, title: "Pagos 100% Seguros", body: "Pago Móvil · Binance · Zinli" },
    { key: "support",  accent: "emerald", Icon: MessageCircle,  iconFill: true,  title: "Soporte Directo",  body: "WhatsApp en tiempo real" },
  ];
  return (
    <div data-testid="trust-badges-strip" className="grid grid-cols-3 sm:grid-cols-3 gap-2 sm:gap-3 max-w-xl">
      {badges.map(({ key, accent, Icon, iconFill, title, body }) => (
        <div
          key={key}
          data-testid={`trust-badge-${key}`}
          className={`flex flex-col sm:flex-row items-center sm:items-center gap-1.5 sm:gap-3 rounded-xl sm:rounded-2xl bg-[var(--ag7-glass)] backdrop-blur-sm border border-${accent}-500/30 px-2 py-2.5 sm:px-4 sm:py-3 hover:border-${accent}-500/60 transition-colors text-center sm:text-left`}
        >
          <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-${accent}-500/10 border border-${accent}-500/40 flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-4 h-4 sm:w-5 sm:h-5 text-${accent}-400`} {...(iconFill ? { fill: "currentColor", strokeWidth: 0 } : {})} />
          </div>
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[11px] font-mono font-black text-white uppercase tracking-wider leading-tight">{title}</p>
            <p className="hidden sm:block text-[10px] text-zinc-400 leading-tight">{body}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* --------------------------------------------------------------------------
 * ¿POR QUÉ ELEGIR AG7 STORE? — vertical trust side-panel (pure presentation).
 * ------------------------------------------------------------------------ */
function WhyAG7() {
  const reasons = [
    { Icon: Zap,             color: "text-ag7-cyan",    ring: "border-ag7-cyan/40 bg-ag7-cyan/10",       text: "Proceso simple y rápido" },
    { Icon: BadgeDollarSign, color: "text-ag7-magenta", ring: "border-ag7-magenta/40 bg-ag7-magenta/10", text: "Precios competitivos" },
    { Icon: ShieldCheck,     color: "text-green-400",   ring: "border-green-500/40 bg-green-500/10",     text: "Transacciones seguras" },
    { Icon: HeadphonesIcon,  color: "text-ag7-purple",  ring: "border-ag7-purple/40 bg-ag7-purple/10",   text: "Soporte personalizado" },
    { Icon: Award,           color: "text-amber-400",   ring: "border-amber-500/40 bg-amber-500/10",     text: "Tu confianza es nuestra prioridad" },
  ];
  return (
    <section
      data-testid="home-why-section"
      className="relative overflow-hidden rounded-3xl bg-[var(--ag7-glass)] backdrop-blur-xl border border-ag7-purple/30 shadow-[0_0_30px_-10px_rgba(147,51,234,0.35)] px-5 py-6"
    >
      <div className="pointer-events-none absolute -top-16 right-0 w-48 h-48 rounded-full bg-ag7-purple/20 blur-3xl" aria-hidden="true" />
      <div className="relative space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-ag7-purple/15 border border-ag7-purple/50 flex items-center justify-center flex-shrink-0" aria-hidden="true">
            <Crown className="w-5 h-5 text-ag7-magenta" />
          </div>
          <h2 className="font-heading font-black text-white text-base leading-tight uppercase">¿Por qué elegir AG7 Store?</h2>
        </div>
        <ul className="space-y-2">
          {reasons.map(({ Icon, color, ring, text }) => (
            <li key={text} className="flex items-center gap-3 rounded-xl bg-ag7-bg/60 border border-ag7-purple/15 px-3 py-2.5 hover:border-ag7-purple/40 transition-colors">
              <span className={`w-7 h-7 rounded-lg border flex items-center justify-center flex-shrink-0 ${ring}`} aria-hidden="true">
                <Icon className={`w-3.5 h-3.5 ${color}`} />
              </span>
              <span className="text-xs font-bold text-zinc-200">{text}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------------
 * ¿CÓMO FUNCIONA? — 3 connected steps (pure presentation).
 * ------------------------------------------------------------------------ */
function HowItWorks() {
  const steps = [
    { n: "01", Icon: Gamepad2,   accent: "from-ag7-purple to-ag7-magenta", glow: "shadow-[0_0_22px_rgba(147,51,234,0.55)]", title: "Selecciona tu Juego", body: "Elige el juego y el paquete que deseas recargar." },
    { n: "02", Icon: CreditCard, accent: "from-ag7-magenta to-ag7-cyan",   glow: "shadow-[0_0_22px_rgba(236,72,153,0.5)]",  title: "Realiza tu Pago",     body: "Paga de forma segura con tu método preferido." },
    { n: "03", Icon: Zap,        accent: "from-ag7-cyan to-ag7-purple",    glow: "shadow-[0_0_22px_rgba(6,182,212,0.5)]",   title: "¡Recarga Lista!",     body: "Tu saldo o diamantes se acreditan al confirmarse el pago." },
  ];
  return (
    <section data-testid="home-how-section" className="max-w-5xl mx-auto space-y-6 sm:space-y-8">
      <div className="flex items-center gap-3 sm:gap-4 px-1">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-ag7-magenta/15 border border-ag7-magenta/50 shadow-[0_0_18px_rgba(236,72,153,0.25)] flex items-center justify-center flex-shrink-0" aria-hidden="true">
          <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-ag7-magenta" />
        </div>
        <div className="text-left">
          <h2 className="font-heading text-xl sm:text-3xl font-black text-white tracking-wide uppercase">¿Cómo Funciona?</h2>
          <p className="text-[11px] sm:text-xs text-[var(--ag7-text-muted)]">Recarga en 3 simples pasos</p>
        </div>
      </div>

      <div className="relative grid sm:grid-cols-3 gap-3 sm:gap-5">
        {/* connecting energy line (desktop) */}
        <div className="hidden sm:block absolute top-1/2 left-[8%] right-[8%] h-px bg-gradient-to-r from-ag7-purple/50 via-ag7-magenta/50 to-ag7-cyan/50 -z-0" aria-hidden="true" />
        {steps.map(({ n, Icon, accent, glow, title, body }, i) => (
          <React.Fragment key={n}>
            <div className="relative z-10 rounded-2xl bg-ag7-surface border border-ag7-purple/20 p-5 sm:p-6 hover:border-ag7-purple/50 hover:-translate-y-1 transition-[border-color,transform,box-shadow] duration-200 hover:shadow-[0_18px_45px_-16px_rgba(147,51,234,0.45)]">
              <div className="flex items-center gap-3 mb-3">
                <span className={`w-11 h-11 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center font-heading font-black text-white text-base ${glow}`} aria-hidden="true">{parseInt(n, 10)}</span>
                <Icon className="w-5 h-5 text-zinc-400" aria-hidden="true" />
              </div>
              <h3 className="font-heading font-extrabold text-white text-sm sm:text-base mb-1">{title}</h3>
              <p className="text-[11px] sm:text-xs text-zinc-400 leading-relaxed">{body}</p>
              <span className="absolute top-4 right-4 font-mono text-4xl font-black text-white/5 select-none" aria-hidden="true">{n}</span>
            </div>
            {i < steps.length - 1 && (
              <ArrowRight className="hidden sm:block absolute top-1/2 -translate-y-1/2 w-6 h-6 text-ag7-magenta drop-shadow-[0_0_8px_rgba(236,72,153,0.7)] z-10" aria-hidden="true"
                style={{ left: `calc(${((i + 1) * 100) / 3}% - 12px)` }} />
            )}
          </React.Fragment>
        ))}
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------------
 * PROMO / BENEFICIOS — commercial side block (generic copy, no invented
 * discounts or stats).
 * ------------------------------------------------------------------------ */
function PromoBlock() {
  return (
    <section
      data-testid="home-promo-section"
      className="relative overflow-hidden rounded-3xl border border-ag7-magenta/40 bg-gradient-to-br from-ag7-purple/25 via-[#12081a] to-ag7-magenta/20 shadow-[0_0_35px_-10px_rgba(236,72,153,0.4)] px-5 py-6"
    >
      <div className="pointer-events-none absolute -bottom-20 right-0 w-56 h-56 rounded-full bg-ag7-magenta/25 blur-3xl" aria-hidden="true" />
      <div className="relative space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-ag7-magenta/15 border border-ag7-magenta/50 flex items-center justify-center flex-shrink-0 shadow-[0_0_25px_rgba(236,72,153,0.35)]" aria-hidden="true">
            <Gift className="w-6 h-6 text-ag7-magenta" />
          </div>
          <h2 className="font-heading font-black text-white text-base uppercase tracking-wide leading-tight">La Experiencia AG7</h2>
        </div>
        <p className="text-xs text-zinc-300 leading-relaxed">
          Recarga tus juegos favoritos de forma rápida y segura. Atención personalizada,
          métodos de pago locales y en divisas, y saldo a favor acreditado en tu cuenta.
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          {["Pago Móvil", "Binance", "Zinli", "Saldo a favor"].map((t) => (
            <span key={t} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-ag7-bg/70 border border-ag7-magenta/25 text-[10px] font-mono font-bold text-zinc-300">
              <CheckCircle2 className="w-3 h-3 text-green-400" aria-hidden="true" /> {t}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------------
 * FOOTER — visual close of the landing (no invented links).
 * ------------------------------------------------------------------------ */
function HomeFooter() {
  const pillars = [
    { Icon: Gamepad2,       label: "Gaming" },
    { Icon: Zap,            label: "Velocidad" },
    { Icon: ShieldCheck,    label: "Seguridad" },
    { Icon: HeadphonesIcon, label: "Soporte" },
    { Icon: Award,          label: "Confianza" },
  ];
  return (
    <footer data-testid="home-footer" className="relative max-w-5xl mx-auto pt-8 pb-2">
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-ag7-purple/60 to-transparent" aria-hidden="true" />
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex flex-wrap justify-center gap-x-5 gap-y-2">
          {pillars.map(({ Icon, label }) => (
            <span key={label} className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-mono font-bold uppercase tracking-wider text-zinc-500 hover:text-zinc-300 transition-colors">
              <Icon className="w-3.5 h-3.5 text-ag7-purple/70" aria-hidden="true" /> {label}
            </span>
          ))}
        </div>
        <p className="text-[10px] sm:text-[11px] font-mono text-zinc-600">© {new Date().getFullYear()} AG7 Store. Todos los derechos reservados.</p>
      </div>
    </footer>
  );
}
